import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useSpring } from 'framer-motion';

const SPRING = { damping: 25, stiffness: 250, mass: 0.5 };

export default function CustomCursor() {
  const [visible, setVisible] = useState(false);
  const [hoverType, setHoverType] = useState('default'); // default | link | card | cta
  const [label, setLabel] = useState('');
  const cursorX = useSpring(0, SPRING);
  const cursorY = useSpring(0, SPRING);
  const isTouchRef = useRef(false);

  const handleMouseMove = useCallback((e) => {
    if (isTouchRef.current) return;
    cursorX.set(e.clientX);
    cursorY.set(e.clientY);
    if (!visible) setVisible(true);
  }, [cursorX, cursorY, visible]);

  const updateHoverState = useCallback((e) => {
    const el = e.target;
    const closest = el.closest('a, button, .venture-card, .service-item, .footer-cta, .nav-link, .mobile-menu-link');
    if (!closest) {
      setHoverType('default');
      setLabel('');
      return;
    }
    if (closest.classList.contains('venture-card')) {
      setHoverType('card');
      setLabel('View');
    } else if (closest.classList.contains('footer-cta')) {
      setHoverType('cta');
      setLabel('');
    } else if (closest.classList.contains('service-item')) {
      setHoverType('link');
      setLabel('');
    } else if (closest.tagName === 'BUTTON' || closest.getAttribute('type') === 'submit') {
      setHoverType('cta');
      setLabel('');
    } else {
      setHoverType('link');
      setLabel('');
    }
  }, []);

  useEffect(() => {
    // Detect touch device
    const onTouch = () => { isTouchRef.current = true; setVisible(false); };
    window.addEventListener('touchstart', onTouch, { once: true });
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseover', updateHoverState);
    window.addEventListener('mouseleave', () => setVisible(false));
    window.addEventListener('mouseenter', () => {
      if (!isTouchRef.current) setVisible(true);
    });

    return () => {
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseover', updateHoverState);
    };
  }, [handleMouseMove, updateHoverState]);

  if (isTouchRef.current) return null;

  const sizes = {
    default: 10,
    link: 40,
    card: 80,
    cta: 60,
  };

  const size = sizes[hoverType] || 10;
  const isExpanded = hoverType !== 'default';

  return (
    <motion.div
      className="custom-cursor"
      data-testid="custom-cursor"
      style={{
        x: cursorX,
        y: cursorY,
        width: size,
        height: size,
        opacity: visible ? 1 : 0,
      }}
      animate={{
        width: size,
        height: size,
        borderColor: isExpanded ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)',
        backgroundColor: isExpanded ? 'rgba(255,255,255,0.06)' : 'transparent',
        mixBlendMode: hoverType === 'card' ? 'difference' : 'normal',
      }}
      transition={{ type: 'spring', ...SPRING }}
    >
      {label && (
        <motion.span
          className="cursor-label"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          data-testid="cursor-label"
        >
          {label}
        </motion.span>
      )}
    </motion.div>
  );
}
